import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from '@mui/joy/Chip';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { KeyboardArrowLeft, KeyboardArrowRight, PhoneCallback, PlayArrowRounded, Voicemail } from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { CircularProgress } from "@mui/joy";
import { useSearchParams, useNavigate } from "react-router-dom";

// --- Import your services and drawers ---
import GeneralApiService from "../../axios/Axios";
import { AxiosPrivate } from "../../axios/Axios";
import DebounceInput from "../../domain/DebounceInput";
import LeadsDrawer from "../modals/lead/LeadsDrawer";
import BookingsDrawer from "../modals/BookingsDrawer";
import {toast, ToastContainer} from "react-toastify";
import { useCall } from "../../lib/call-context";
import ResponsiveTableWrapper, { ResponsiveFilterBar, FilterBarLeft } from "../ui/ResponsiveTableWrapper";

// Local storage key for category filter (shared across messages/voicemails/call logs)
const CATEGORY_FILTER_KEY = 'messagesCategoryFilter';

// Interface definitions (unchanged)
interface Row {
    id: number;
    callSid?: string | null;
    fromNumber: string;
    toNumber: string;
    duration?: number;
    callType: 'inbound' | 'outbound';
    answerStatus: 'answered' | 'not_answered';
    timestamp: string;
    customer?: {
        id: number;
        full_name: string;
        phone: string;
    } | null;
    calledByUsername?: string | null;
    latestLeadId?: number | null;
    latestBookingId?: number | null;
    voicemailId?: number | null;
    callCategory?: string;
    hasTranscription?: boolean;
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-GB', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }
    return 'No Valid Date';
}

function formatDuration(durationInSeconds?: number): string {
    if (durationInSeconds === undefined || durationInSeconds === null || durationInSeconds < 0) {
        return '0:00';
    }

    const totalSeconds = Math.floor(durationInSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Row Component (unchanged)
function CallLogRow(props: {
    row: Row;
    onOpenLead: (leadId: number) => void;
    onOpenBooking: (bookingId: number) => void;
    onTranscriptionQueued: (callLogId: number) => void;
}) {
    const { row, onOpenLead, onOpenBooking, onTranscriptionQueued } = props;
    const { makeCall } = useCall();
    const navigate = useNavigate();


    const handleCallCustomer = async () => {
        const phoneToCall = row.customer?.phone || (row.callType === 'inbound' ? row.fromNumber : row.toNumber);
        const nameToUse = row.customer?.full_name || "Unknown";

        if (phoneToCall) {
            makeCall(phoneToCall, nameToUse);
        } else {
            toast.error("Phone number not available", { containerId: "call-log-table" });
        }
    };

    const handlePlayRecording = () => {
        if (!row.callSid) {
            toast.error("Recording not available for this call", { containerId: "call-log-table" });
            return;
        }

        const recordingUrl = new URL(
            `marketing/recordings/${row.callSid}/`,
            AxiosPrivate.defaults.baseURL || window.location.origin
        ).toString();
        window.open(recordingUrl, "_blank", "noopener,noreferrer");
    };

    const handleQueueTranscription = async () => {
        try {
            await GeneralApiService.queueCallTranscription(row.id);
            toast.success("Transcription queued", { containerId: "call-log-table" });
            onTranscriptionQueued(row.id);
        } catch (e: any) {
            const msg = e?.response?.data?.error || e?.response?.data?.detail || "Failed to queue transcription";
            toast.error(msg, { containerId: "call-log-table" });
        }
    };

    return (
        <tr>
            <td>{formatDate(row.timestamp)}</td>
            <td>{row.callType === 'inbound' ? row.fromNumber : row.toNumber}</td>
            <td>{formatDuration(row.duration)}</td>
            <td>{row.customer?.full_name || <Typography textColor="neutral.500">Unknown</Typography>}</td>
            <td>{row.calledByUsername || <Typography textColor="neutral.500">Unknown</Typography>}</td>
            <td>
                <Chip size="sm" variant="outlined" color={row.callCategory === 'Existing Booking' ? 'success' : 'neutral'}>
                    {row.callCategory || 'Sales'}
                </Chip>
            </td>
            <td>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                        size="sm"
                        variant="soft"
                        color={row.answerStatus === 'answered' ? 'success' : 'danger'}
                    >
                        {row.answerStatus.replace(/_/g, ' ')}
                    </Chip>
                    <Chip
                        size="sm"
                        variant="outlined"
                        color={row.callType === 'inbound' ? 'primary' : 'warning'}
                    >
                        {row.callType}
                    </Chip>
                </Box>
            </td>
            <td>
                 {row.voicemailId ? (
                    <Button
                        size="sm"
                        variant="soft"
                        color="primary"
                        startDecorator={<Voicemail />}
                        onClick={() => navigate(`/dashboard/voicemails?id=${row.voicemailId}`)}
                    >
                        Left Voicemail
                    </Button>
                ) : (
                    <Typography level="body-xs" textColor="neutral.500">No Voicemail</Typography>
                )}
            </td>
            <td>
                {row.hasTranscription ? (
                    <Typography level="body-sm" textColor="success.600">Yes</Typography>
                ) : (
                    <Typography level="body-sm" textColor="neutral.500">No</Typography>
                )}
            </td>
            <td>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {row.latestLeadId && (
                        <Button  size="sm"  color="primary" onClick={() => onOpenLead(row.latestLeadId!)}>
                            Open Latest Lead
                        </Button>
                    )}
                    {row.latestBookingId && (
                        <Button  size="sm" color="primary" onClick={() => onOpenBooking(row.latestBookingId!)}>
                            Open Latest booking
                        </Button>
                    )}
                    <IconButton variant="solid" size="sm" color="primary" onClick={handleCallCustomer}>
                        <PhoneCallback />
                    </IconButton>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<PlayArrowRounded />}
                        onClick={handlePlayRecording}
                        disabled={!row.callSid}
                    >
                        Play
                    </Button>
                    {!row.hasTranscription && (
                        <Button
                            size="sm"
                            variant="soft"
                            color="warning"
                            onClick={handleQueueTranscription}
                            disabled={!row.callSid}
                        >
                            Transcribe
                        </Button>
                    )}
                </Box>
            </td>
        </tr>
    );
}

// mapping function (unchanged)
const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => ({
        id: item.id,
        callSid: item.call_sid,
        fromNumber: item.from_number,
        toNumber: item.to_number,
        duration: item.duration,
        callType: item.call_type,
        answerStatus: item.answer_status,
        timestamp: item.timestamp,
        customer: item.customer ? { id: item.customer.id, full_name: item.customer.full_name, phone: item.customer.phone  } : null,
        calledByUsername: item.called_by_username,
        latestLeadId: item.latest_lead_id,
        latestBookingId: item.latest_booking_id,
        voicemailId: item.voicemail_id,
        callCategory: item.call_category,
        hasTranscription: !!item.has_transcription,
    }));
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

// --- Main Table Component ---
export default function CallLogTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(15);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    // Category filter with localStorage persistence
    const [category, setCategory] = useState(() => {
        return localStorage.getItem(CATEGORY_FILTER_KEY) || 'All';
    });
    const [urlSearchParam] = useSearchParams();
    
    // Update localStorage when category changes
    const handleCategoryChange = useCallback((newCategory: string) => {
        setCategory(newCategory);
        localStorage.setItem(CATEGORY_FILTER_KEY, newCategory);
        setPage(0);
    }, []);

    // State for Modals/Drawers
    const [openLeadId, setOpenLeadId] = useState<number | null>(null);
    const [openLeadModal, setOpenLeadModal] = useState(false);
    const [openBookingId, setOpenBookingId] = useState<number | null>(null);
    const [openBookingModal, setOpenBookingModal] = useState(false);

    const handleTranscriptionQueued = (callLogId: number) => {
        setRows(prev => prev.map(r => r.id === callLogId ? { ...r, hasTranscription: true } : r));
    };

    const getLabelDisplayedRowsTo = () => Math.min(pageCount, (page + 1) * rowsPerPage);

    // 1. Extract data fetching into a reusable function
    const fetchData = async () => {
        setLoadingData(false);
        try {
            const callsData = await GeneralApiService.getAllCalls(page + 1, search, category);
            setRows(mapRawDataToRow(callsData.results || []));
            setPageCount(callsData.count || 0);
        } catch (error) {
            console.error("Failed to fetch call logs:", error);
            setRows([]);
            setPageCount(0);
        } finally {
            setLoadingData(true);
        }
    };

    // 2. useEffect now calls the fetchData function
    useEffect(() => {
        fetchData();
    }, [page, search, category]);

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };

    const handleOpenLeadModal = (leadId: number) => {
        setOpenLeadId(leadId);
        setOpenLeadModal(true);
    };

    // 3. Update the handleCloseLeadModal callback
    const handleCloseLeadModal = (updateInfo?: { id: number }) => {
        setOpenLeadModal(false);
        setOpenLeadId(null);
        // If updateInfo exists, a save occurred. Refresh the table data.
        if (updateInfo) {
            fetchData();
        }
    };

    const handleOpenBookingModal = (bookingId: number) => {
        setOpenBookingId(bookingId);
        setOpenBookingModal(true);
    };

    // 4. Update the handleCloseBookingModal callback
    const handleCloseBookingModal = (updateInfo?: { id: number }) => {
        setOpenBookingModal(false);
        setOpenBookingId(null);
        // If updateInfo exists, a save occurred. Refresh the table data.
        if (updateInfo) {
            fetchData();
        }
    };

    const colSpan = 10;

    return (
        <>
            <ToastContainer containerId={"call-log-table"} position="top-right" autoClose={5000} theme="dark" />
            <ResponsiveFilterBar>
                <FilterBarLeft>
                    <DebounceInput 
                        sx={{ width: { xs: '100%', sm: '250px' } }} 
                        startDecorator={<SearchRoundedIcon />}
                        placeholder="Search by number..."
                        debounceTimeout={1000}
                        defaultValue={urlSearchParam.get("search") as string}
                        handleDebounce={handleDebounce}
                    />
                    <Select 
                        value={category} 
                        onChange={(_, val) => handleCategoryChange(val || 'All')} 
                        sx={{ width: { xs: '100%', sm: 200 } }}
                    >
                        <Option value="All">All Categories</Option>
                        <Option value="Sales">Sales</Option>
                        <Option value="Existing Booking">Existing Booking</Option>
                    </Select>
                </FilterBarLeft>
            </ResponsiveFilterBar>
            <ResponsiveTableWrapper minWidth={900}>
                <Table hoverRow sx={{ '& tr > *:last-child': { textAlign: 'right' } }}>
                    <thead>
                    <tr>
                        <th style={{ width: '15%' }}>Date</th>
                        <th style={{ width: '14%' }}>Caller/Recipient</th>
                        <th style={{ width: '8%' }}>Duration</th>
                        <th style={{ width: '15%' }}>Customer Name</th>
                        <th style={{ width: '12%' }}>Called By</th>
                        <th style={{ width: '10%' }}>Category</th>
                        <th style={{ width: '12%' }}>Status & Type</th>
                        <th style={{ width: '13%' }}>Voicemail</th>
                        <th style={{ width: '8%' }}>Transcribed</th>
                        <th style={{ width: '16%' }}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loadedData ? (
                        rows.map((row, index) => (
                            <CallLogRow
                                key={index}
                                row={row}
                                onOpenLead={handleOpenLeadModal}
                                onOpenBooking={handleOpenBookingModal}
                                onTranscriptionQueued={handleTranscriptionQueued}
                            />
                        ))
                    ) : (
                        <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                    )}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={colSpan}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                    {labelDisplayedRows({
                                        from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                        to: getLabelDisplayedRowsTo(),
                                        count: pageCount,
                                    })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                    </tfoot>
                </Table>
            </ResponsiveTableWrapper>

            {/* --- Render Drawers with updated callbacks --- */}
            <React.Fragment>
                {openLeadModal && <LeadsDrawer key={`leads-modal-${openLeadId}`} openState={openLeadModal} leadId={openLeadId!} parentCallback={handleCloseLeadModal}/>}
                {openBookingModal && <BookingsDrawer key={`bookings-modal-${openBookingId}`} openState={openBookingModal} newBooking={null} newBookingValue={null} bookingId={openBookingId!} parentCallback={handleCloseBookingModal}/>}
            </React.Fragment>
        </>
    );
}
